/**
 * react-intl v2 compatibility shim for @ps-design-system.
 *
 * The design system is built against react-intl v5+, which breaks here in two
 * ways because web-hosting is pinned to react-intl 2.9.0:
 *
 *   1. Its components import `useIntl`, added in v3. v2 has no such export, so
 *      the ESM import fails to link ("does not provide an export named
 *      'useIntl'") the moment the module is loaded — whether or not the hook is
 *      ever called.
 *   2. It ships `defaultMessage` pre-compiled to a @formatjs AST array
 *      (`[{ type: 0, value: "..." }]`). v2's formatter only accepts strings and
 *      throws "A message must be provided as a String or AST." on an array.
 *      DS components only render today because their hashed message ids resolve
 *      against the intl catalogs, so the AST is never consulted — any id that
 *      is missing from the catalogs hard-throws.
 *
 * This module re-exports react-intl 2.9.0 unchanged except for:
 *
 *   - `IntlProvider`, which additionally publishes v2's legacy-context `intl`
 *     object on a modern React context so hooks can reach it, and
 *   - `useIntl`, which returns that object with an AST-aware `formatMessage`.
 *
 * vite.config.js rewrites `react-intl` to this file for the ps-design-system
 * bundles and for sharedIntlProviderHOC (the shared-components file that owns
 * the app's IntlProvider). App code imports it as `react-intl-compat`.
 */
import {
  cloneElement,
  createContext,
  createElement,
  isValidElement,
  useContext,
} from "react";
import { IntlProvider as BaseIntlProvider, injectIntl } from "react-intl";

export * from "react-intl";

// @formatjs AST node types emitted by the design system. Plural/select nodes
// (5/6/7) exist elsewhere in the DS bundle but never in a message web-hosting
// renders; they'd need real ICU support, i.e. a react-intl upgrade.
const AST_LITERAL = 0;
const AST_ARGUMENT = 1;
const AST_TAG = 8;

const containsTag = (ast) => ast.some((node) => AST_TAG === node.type);

const renderAst = (ast, values) =>
  ast.map((node, index) => {
    switch (node.type) {
    case AST_LITERAL:
      return node.value;
    case AST_ARGUMENT:
      return values[node.value];
    case AST_TAG: {
      // v5+ hands tag handlers the formatted children as chunks.
      const renderChunks = values[node.value];
      const children = renderAst(node.children, values);
      const rendered =
          "function" === typeof renderChunks ? renderChunks(children) : children;
      return isValidElement(rendered)
        ? cloneElement(rendered, { key: index })
        : rendered;
    }
    default:
      throw new Error(
        `[react-intl-compat] Unsupported @formatjs AST node type ${node.type}. ` +
            "Only literals, simple arguments and tags are handled.",
      );
    }
  });

const formatAst = (ast, values = {}) => {
  const parts = renderAst(ast, values);
  // Collapse to a string when nothing rendered to an element — callers pass the
  // result to `placeholder`, zod `message`, etc., which expect a plain string.
  return parts.every(
    (part) => "string" === typeof part || "number" === typeof part,
  )
    ? parts.join("")
    : parts;
};

const astSafeIntlCache = new WeakMap();

/**
 * Wraps a v2 `intl` object so `formatMessage` tolerates an AST `defaultMessage`.
 * Cached per `intl` instance so the value stays referentially stable across
 * renders (DS components list `intl` in hook dependency arrays).
 */
export const toAstSafeIntl = (intl) => {
  const cached = astSafeIntlCache.get(intl);
  if (cached) return cached;

  const astSafeIntl = {
    ...intl,
    formatMessage: (descriptor, values) => {
      const defaultMessage = descriptor && descriptor.defaultMessage;
      if (!Array.isArray(defaultMessage)) {
        return intl.formatMessage(descriptor, values);
      }

      // Prefer the catalog's translation, but only for tag-free messages: v2 has
      // no rich-text support and would emit `<atag>` markup literally.
      const translated = intl.messages && intl.messages[descriptor.id];
      if ("string" === typeof translated && !containsTag(defaultMessage)) {
        return intl.formatMessage(
          { ...descriptor, defaultMessage: translated },
          values,
        );
      }

      return formatAst(defaultMessage, values);
    },
  };

  astSafeIntlCache.set(intl, astSafeIntl);
  return astSafeIntl;
};

const IntlCompatContext = createContext(null);

// v2 exposes `intl` only on legacy context, which hooks cannot read. Bridge it
// onto a modern context from inside the provider.
const IntlCompatBridge = injectIntl(({ children, intl }) =>
  createElement(
    IntlCompatContext.Provider,
    { value: toAstSafeIntl(intl) },
    children,
  ),
);

export const IntlProvider = ({ children, ...props }) =>
  createElement(
    BaseIntlProvider,
    props,
    createElement(IntlCompatBridge, null, children),
  );

export const useIntl = () => {
  const intl = useContext(IntlCompatContext);
  if (!intl) {
    throw new Error(
      "[react-intl-compat] useIntl() was called outside an IntlProvider. The " +
        "provider must come from this shim (see vite.config.js) so the v2 intl " +
        "object is published on the modern context this hook reads.",
    );
  }
  return intl;
};
